import React from "react";
import {
  FaInfoCircle,
  FaClock,
  FaCalendarAlt,
  FaCheckCircle,
  FaPaperPlane,
} from "react-icons/fa";

const CampaignDetails = ({
  campaign,
  onChange,
  onWorkingHoursChange,
  onDaySelection,
}) => {
  // Week days for selection
  const weekDays = [
    { id: "Su", label: "Su" },
    { id: "Mo", label: "Mo" },
    { id: "Tu", label: "Tu" },
    { id: "We", label: "We" },
    { id: "Th", label: "Th" },
    { id: "Fr", label: "Fr" },
    { id: "Sa", label: "Sa" },
  ];

  // Time zones (simplified list)
  const timeZones = [
    "(GMT+01:00) Central European Time - Belgrade, Central...",
    "(GMT+00:00) Greenwich Mean Time - Dublin, Edinburgh, London",
    "(GMT-05:00) Eastern Time, Bogota, Lima",
    "(GMT-08:00) Pacific Time - Los Angeles, Seattle",
    "(GMT+09:00) Japan Standard Time - Tokyo, Osaka",
    "(GMT+05:30) India Standard Time - Mumbai, New Delhi",
  ];

  // City-specific presets for working hours
  const cityPresets = [
    {
      city: "New York",
      workingHours: "Working hours",
      days: "Weekdays",
    },
    {
      city: "Tokyo",
      workingHours: "Working hours",
      days: "Weekdays",
    },
    {
      city: "San Francisco",
      workingHours: "Working hours",
      days: "Weekdays",
    },
    {
      city: "London",
      workingHours: "Working hours",
      days: "Weekdays",
    },
  ];

  // 5-touch sequence from specification
  const touchSequence = [
    {
      dayOffset: 0,
      name: "Spark",
      goal: "Introduce and highlight one key result",
      ctaType: "Quick chat suggestion",
    },
    {
      dayOffset: 3,
      name: "Value Add",
      goal: "Share mini case study and video link",
      ctaType: "Offer to share calculator/tool",
    },
    {
      dayOffset: 6,
      name: "Pain/Insight",
      goal: "Highlight hidden costs, ask engaging question",
      ctaType: "Offer to share breakdown/analysis",
    },
    {
      dayOffset: 10,
      name: "Social Proof",
      goal: "Share quantified peer outcome, offer calendar link",
      ctaType: "Meeting calendar link",
    },
    {
      dayOffset: 14,
      name: "Break-up",
      goal: "Friendly final outreach with easy out",
      ctaType: "Closing the loop",
    },
  ];

  // Apply a city preset
  const applyPreset = (city) => {
    // This would normally load preset values for the specific city
    // For this demo, we'll just show an alert
    alert(`Applied preset for ${city}`);
  };

  // Helper function to format Date object to YYYY-MM-DD for input[type="date"]
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  // Handle date change
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    onChange({
      target: {
        name,
        value: new Date(value),
      },
    });
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Campaign details</h2>

      {/* Campaign Name */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name
        </label>
        <input
          type="text"
          name="name"
          value={campaign.name}
          onChange={onChange}
          placeholder="Test"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
        />
      </div>

      {/* Goal and Calendly Link */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Goal
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <select
            name="goal"
            value={campaign.goal}
            onChange={onChange}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="Book a demo">Book a demo</option>
            <option value="Get a reply">Get a reply</option>
            <option value="Schedule a call">Schedule a call</option>
            <option value="Download whitepaper">Download whitepaper</option>
          </select>

          <input
            type="text"
            name="calendlyLink"
            value={campaign.calendlyLink}
            onChange={onChange}
            placeholder="https://calendly.com/aisdr-intro/demo"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        <div className="mt-2 flex items-start text-sm text-blue-600">
          <FaInfoCircle className="mt-0.5 mr-2 flex-shrink-0" />
          <p>
            Example: Book a demo using this calendly link
            https://calendly.com/aisdr-intro/demo
          </p>
        </div>
      </div>

      {/* 5-Touch Sequence */}
      <div className="mb-8 bg-purple-50 p-4 rounded-lg border border-purple-100">
        <h3 className="text-lg font-medium text-purple-800 mb-3 flex items-center">
          <FaPaperPlane className="mr-2" />
          5-Touch Campaign Sequence
        </h3>
        <p className="text-sm text-purple-700 mb-4">
          This campaign follows our optimized 5-touch sequence over 14 days to
          maximize response rates.
        </p>

        <div className="space-y-3">
          {touchSequence.map((touch, index) => (
            <div
              key={index}
              className="bg-white p-3 rounded-md border border-purple-200 shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-purple-900">
                    Day {touch.dayOffset}: {touch.name}
                  </h4>
                  <p className="text-sm text-gray-600">{touch.goal}</p>
                </div>
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                  {touch.ctaType}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Campaign Duration */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-700 mb-4">
          Campaign Duration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center mb-1">
              <FaCalendarAlt className="mr-2 text-gray-400" />
              <label className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
            </div>
            <input
              type="date"
              name="startDate"
              value={formatDateForInput(campaign.startDate)}
              onChange={handleDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <div className="flex items-center mb-1">
              <FaCalendarAlt className="mr-2 text-gray-400" />
              <label className="block text-sm font-medium text-gray-700">
                End Date
              </label>
            </div>
            <input
              type="date"
              name="endDate"
              value={formatDateForInput(campaign.endDate)}
              onChange={handleDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
        </div>
        <div className="mt-2 flex items-start text-sm text-blue-600">
          <FaInfoCircle className="mt-0.5 mr-2 flex-shrink-0" />
          <p>
            The campaign will be active between these dates. Emails will only be
            sent during this period.
          </p>
        </div>
      </div>

      {/* Stop sending on reply */}
      <div className="mb-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="stopOnReply"
            checked={campaign.stopOnReply !== false}
            onChange={(e) =>
              onChange({
                target: {
                  name: "stopOnReply",
                  value: e.target.checked,
                },
              })
            }
            className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <label htmlFor="stopOnReply" className="ml-2 text-sm text-gray-700">
            Stop sequence if lead replies to any email
          </label>
        </div>
      </div>

      {/* Planned Send Time */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-700 mb-4">
          Planned send time
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Custom Settings */}
          <div>
            <h4 className="text-base font-medium mb-4">
              Create custom settings
            </h4>

            {/* Time Zone */}
            <div className="mb-4">
              <div className="flex items-center mb-1">
                <FaClock className="mr-2 text-gray-400" />
                <label className="block text-sm font-medium text-gray-700">
                  Time zone
                </label>
              </div>
              <select
                name="timezone"
                value={campaign.timezone}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                {timeZones.map((zone, index) => (
                  <option key={index} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </div>

            {/* Choose Day or Days */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose day or days
              </label>
              <div className="flex space-x-2">
                {weekDays.map((day) => (
                  <button
                    key={day.id}
                    type="button"
                    onClick={() => onDaySelection(day.id)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                      campaign.sendingDays.includes(day.id)
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Working Hours */}
            <div>
              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map(
                (day) =>
                  campaign.sendingDays.includes(day.slice(0, 2)) && (
                    <div key={day} className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {day}
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={campaign.workingHours[day].start}
                          onChange={(e) =>
                            onWorkingHoursChange(day, "start", e.target.value)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <span className="text-gray-500">-</span>
                        <input
                          type="time"
                          value={campaign.workingHours[day].end}
                          onChange={(e) =>
                            onWorkingHoursChange(day, "end", e.target.value)
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>

          {/* Ready-made Settings */}
          <div>
            <h4 className="text-base font-medium mb-4">
              Select ready-made settings
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cityPresets.map((preset, index) => (
                <div
                  key={index}
                  onClick={() => applyPreset(preset.city)}
                  className="bg-gray-50 border border-gray-200 p-4 rounded-lg cursor-pointer hover:border-purple-300"
                >
                  <h5 className="font-medium text-gray-800">{preset.city}</h5>
                  <p className="text-sm text-gray-600">{preset.workingHours}</p>
                  <p className="text-sm text-gray-600">{preset.days}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetails;
